// assets/collection-pager.js
// <collection-pager> — divides a product grid into display pages on the client.
//
// The server hands us products in fixed-size network pages; a shopper reads
// display pages of their own chosen size (see the Network page, Display page and
// Page size entries in CONTEXT.md, and docs/adr/0002 for why this cannot live on
// the server at all). This element owns the whole of that translation: it decides
// which network pages a requested display page needs, fetches the missing ones in
// parallel, and shows exactly the products in range.
//
// Markup:
//   <collection-pager
//     data-page-size="20"                              merchant's starting size
//     data-page-size-options="20,40"                    sizes a shopper may pick
//     data-network-page-size="{{ paginate.page_size }}"  products per network page
//     data-network-pages="{{ paginate.pages }}"          how many of them exist
//     data-network-page="{{ paginate.current_page }}"    which one the server sent
//     data-total-products="{{ paginate.items }}"         products in the collection
//   ></collection-pager>
//
// The element renders its own pager into itself and hides the server-rendered one
// while it is in charge, so nothing is mutated in place and handing the grid back
// is just a matter of unhiding. With JavaScript off it never runs and the
// server-rendered pager reaches every product through real links.
//
// State lives in the URL on parameters of our own: `per_page` for the size and
// `dp` for the display page. `page` is left alone — Liquid's paginate tag
// consumes it, so reusing it would make the server render the wrong products.
//
// A numeric size outlives the URL too: it is kept in local storage, so a shopper
// who prefers 40 sets it once and keeps it across collections and across visits.
// "All" is deliberately never kept — see setPageSize().
//
// Two ways to divide the products:
//
//   Plain      — a product's position in the collection is known from its
//                network page, so only the network pages spanning the
//                requested display page are ever fetched.
//   Filtered   — an eligibility provider (the Showroom) decides which products
//                a shopper may see, and that verdict needs every product in
//                hand. Every network page is gathered, in parallel, once.
//
// "All" is not a third way. It is a display page as large as the collection, so
// the same arithmetic yields one page holding everything and — since there is
// only one — no pager. What it does need is care over how the products land: a
// few hundred cards revealed in a single pass stalls the main thread, so a large
// reveal is spread across animation frames instead.
(function () {
  const DEFAULT_NETWORK_PAGE_SIZE = 50;
  const DEFAULT_PAGE_SIZE = 20;
  const DEFAULT_LOADING_LABEL = 'Loading products...';
  const SPINNER_CLASS = 'collection-pager-spinner';

  // A page size like any other, and deliberately unlimited — there is no hidden
  // ceiling on how many products "all" will show.
  const ALL_PAGE_SIZE = 'all';

  // Past this many display changes in one pass, the work is spread across
  // animation frames rather than done all at once. The largest numeric
  // transition — a full page out and a full page in at the largest offered size
  // — stays under it on purpose, so choosing 20 or 40 remains a single
  // synchronous pass. Offer a size large enough to break that and the chunking
  // starts applying to it too, which is the right answer.
  const INCREMENTAL_REVEAL_THRESHOLD = 100;
  const REVEAL_CHUNK_SIZE = 25;

  // Ours, not the platform's. See the note above.
  const PAGE_SIZE_PARAM = 'per_page';
  const DISPLAY_PAGE_PARAM = 'dp';
  const NETWORK_PAGE_PARAM = 'page';

  // Where a remembered size lives. Local storage rather than session storage, and
  // deliberately alongside the retail-mode preference theme.liquid keeps under
  // `_retail_mode_preview`: a preference that lasted only until the browser closed
  // would have to be re-picked tomorrow, which is most of the friction this is
  // here to remove.
  const STORED_PAGE_SIZE_KEY = '_page_size';

  // Storage is an enhancement, never a dependency. Private browsing can refuse it
  // outright, a full quota can refuse a single write, and either way the shopper
  // gets the merchant's default and a working grid rather than an error — so both
  // ends are wrapped and neither reports anything.
  function readStoredPageSize() {
    try {
      return window.localStorage.getItem(STORED_PAGE_SIZE_KEY);
    } catch (e) {
      return null;
    }
  }

  function storePageSize(size) {
    try {
      window.localStorage.setItem(STORED_PAGE_SIZE_KEY, String(size));
    } catch (e) {
      // A preference we cannot keep is not worth interrupting a shopper over.
    }
  }

  // Ordered fallbacks — collection, search, and the standalone products grid all
  // mark their list differently, and older templates mark it not at all.
  const LIST_SELECTORS = [
    '[data-product-grid-list]',
    '.collection-product-list',
    'ul.products-grid',
    'ul[class*="product"]',
    '.product-grid ul',
    '#product-grid ul'
  ];

  // Fetched documents are parsed detached, so only the explicit markers are safe
  // to match against — the looser selectors above can hit unrelated lists.
  const FETCHED_LIST_SELECTORS = ['[data-product-grid-list]', '.collection-product-list', 'ul.products-grid'];

  /**
   * Eligibility provider contract
   * -----------------------------
   * A single owner of the question "which of these products may this shopper
   * see?" registers itself once:
   *
   *   CollectionPager.setEligibilityProvider({
   *     isActive() { ... },
   *     loadingLabel: 'Loading showroom...',
   *     async eligible(items, { force }) { ... }
   *   })
   *
   * isActive() -> boolean
   *   Whether the provider is filtering right now. While it returns false the
   *   element divides the products itself, unfiltered. Re-checked after every
   *   await, so returning false mid-flight cancels the pass.
   *
   * loadingLabel -> string (optional)
   *   Wording for the spinner shown while products are being gathered.
   *
   * eligible(items, { force }) -> Promise<Element[] | null>
   *   Given every product element currently in the grid, across all network
   *   pages, resolve to the subset the shopper may see. Resolve to null to mean
   *   "no opinion — all of them"; that is the fallback path, not an empty grid.
   *   `force` asks the provider to bypass any cache it keeps.
   *
   * The provider is never told about page numbers, page sizes, or the DOM beyond
   * the items handed to it. Everything else is this element's business.
   *
   * After each pass the element dispatches a bubbling `collection-pager:render`
   * event, so a provider (or anything else) can react to products becoming
   * visible without owning the pagination.
   */
  let eligibilityProvider = null;

  function strings() {
    return window.collectionPagerStrings || {};
  }

  // Translated copy reaches JS with bracketed placeholders, matching the
  // `unavailable_with_option` convention already in theme.liquid.
  function interpolate(template, values) {
    return Object.keys(values).reduce(
      (out, key) => out.split('[' + key + ']').join(String(values[key])),
      String(template)
    );
  }

  function networkPageUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set(NETWORK_PAGE_PARAM, String(page));
    // Our parameters mean nothing to the server, and leaving them on would give
    // the same network page a different URL for every display state it was fetched
    // from — one per size, per page — none of them cacheable against another.
    url.searchParams.delete(PAGE_SIZE_PARAM);
    url.searchParams.delete(DISPLAY_PAGE_PARAM);
    return url.toString();
  }

  function positiveInt(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  // Yields to the browser so it can lay out and paint what has just been
  // revealed before the next batch of it arrives.
  function nextFrame() {
    return new Promise((resolve) => {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => resolve());
      } else {
        window.setTimeout(resolve, 16);
      }
    });
  }

  function firstMatch(root, selectors) {
    for (const selector of selectors) {
      const found = root.querySelector(selector);
      if (found) return found;
    }
    return null;
  }

  // 1 … 4 5 6 … 20 — first, last, and the current page's neighbours.
  function paginationParts(totalPages, currentPage) {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const normalized = Array.from(pages)
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);

    const parts = [];
    normalized.forEach((page, index) => {
      parts.push(page);
      const next = normalized[index + 1];
      if (next && next - page > 1) parts.push('...');
    });
    return parts;
  }

  class CollectionPager extends HTMLElement {
    constructor() {
      super();
      this.requestToken = 0;
      this.currentPage = 1;
      this.totalPages = 1;
      this.pageSize = DEFAULT_PAGE_SIZE;
      // What the size was when this document loaded, which is what the history
      // entry it loaded on stands for — see restoredPageSize().
      this.loadedPageSize = DEFAULT_PAGE_SIZE;
      // network page -> the product elements that arrived in it
      this.networkPageItems = new Map();
      this.inFlight = new Map();
      // Set only while a provider is filtering; null means "divide by position".
      this.eligibleItems = null;
      // The Showroom's "show all products" toggle, distinct from the "All" page
      // size — see showEverything().
      this.showingEverything = false;
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.pageSize = this.readPageSize();
      this.loadedPageSize = this.pageSize;
      this.currentPage = this.readDisplayPage();
      window.addEventListener('popstate', this.onPopState);

      this.adoptServerItems();

      // Deferred scripts all run before any timer does, so a provider that has
      // not registered yet still gets to claim the grid before we fetch
      // anything for it. The pass below only divides what the server already
      // sent, which keeps the first paint free of both flashes and requests;
      // anything needing the network waits for the task after it. Its promise is
      // deliberately dropped — a first paint never has enough products in hand
      // to reach the chunked path, so it settles within this task anyway.
      if (this.ownsPagination) this.divideLoaded();
      window.setTimeout(() => {
        if (!this.isConnected) return;
        if (this.filtering()) return; // the provider drives its own pass
        if (!this.ownsPagination) return; // native pagination keeps this grid
        this.apply();
      }, 0);
    }

    disconnectedCallback() {
      // Cancel anything in flight; the replacement element starts clean.
      this.requestToken += 1;
      window.removeEventListener('popstate', this.onPopState);
      this.hideSpinner();
    }

    get grid() {
      return this.closest('#ProductGridContainer') || document.getElementById('ProductGridContainer');
    }

    get networkPageSize() {
      return positiveInt(this.dataset.networkPageSize, DEFAULT_NETWORK_PAGE_SIZE);
    }

    get networkPages() {
      return positiveInt(this.dataset.networkPages, 1);
    }

    get networkPage() {
      return positiveInt(this.dataset.networkPage, 1);
    }

    // The sizes a shopper may choose, numbers plus possibly ALL_PAGE_SIZE. Empty
    // when no control is offered, in which case the merchant's setting stands
    // unvalidated — that is the search page, which paginates at its own size and
    // has no dropdown.
    get pageSizeOptions() {
      return (this.dataset.pageSizeOptions || '')
        .split(',')
        .map((value) => {
          const token = value.trim();
          return token === ALL_PAGE_SIZE ? ALL_PAGE_SIZE : positiveInt(token, null);
        })
        .filter((value) => value !== null);
    }

    // The numeric sizes only. "All" is never a starting size: nobody should
    // inherit an expensive page load from a setting they cannot see.
    get numericPageSizeOptions() {
      return this.pageSizeOptions.filter((option) => option !== ALL_PAGE_SIZE);
    }

    // The merchant's per-page setting is the starting size. A stored value that
    // isn't offered falls back to the first numeric option rather than rendering
    // a size nobody can pick.
    get startingPageSize() {
      const setting = positiveInt(this.dataset.pageSize, null);
      const options = this.numericPageSizeOptions;
      if (options.length === 0) return setting || DEFAULT_PAGE_SIZE;
      if (setting && options.includes(setting)) return setting;
      return options[0];
    }

    // Whether the shopper asked for the whole collection on one page.
    get showAllProducts() {
      return this.pageSize === ALL_PAGE_SIZE;
    }

    // The size the arithmetic runs on. "All" is a display page as large as the
    // collection, which makes everything downstream — one page, every product in
    // range, no pager — fall out of the same sums the numeric sizes use.
    get effectivePageSize() {
      if (this.showAllProducts) return Math.max(1, this.totalProducts);
      return this.pageSize;
    }

    get totalProducts() {
      if (this.eligibleItems) return this.eligibleItems.length;
      return positiveInt(this.dataset.totalProducts, this.loadedItems().length);
    }

    filtering() {
      return !!(eligibilityProvider && eligibilityProvider.isActive());
    }

    // Whether this grid wants its products divided into shopper-sized display
    // pages at all. Offering sizes is what says so — a grid with none has no
    // control, no choice to honour, and no reason to take pagination off the
    // server. Search is the case that matters: its pager covers articles and
    // pages as well as products, so taking it over would strand the rest of the
    // results. A provider filtering the grid is a separate matter and overrides
    // this, since filtered products have to be re-divided wherever they are.
    get ownsPagination() {
      return this.pageSizeOptions.length > 0;
    }

    // -------------------------------------------------------------- URL state

    urlParams() {
      return new URLSearchParams(window.location.search);
    }

    // The size this candidate actually asks for, or null where it asks for
    // nothing this grid can honour. Where no sizes are offered at all there is no
    // control and therefore no choice to read: nothing is honourable, so a
    // hand-written per_page and a stored preference are both ignored and the
    // merchant's setting stands.
    //
    // A stored value is put through exactly the same test as a URL parameter, so
    // one left behind by a size that is no longer offered simply stops counting
    // rather than rendering a size nobody can pick.
    honourablePageSize(candidate) {
      if (candidate === null || candidate === undefined || candidate === '') return null;
      const options = this.pageSizeOptions;
      if (String(candidate) === ALL_PAGE_SIZE) {
        return options.includes(ALL_PAGE_SIZE) ? ALL_PAGE_SIZE : null;
      }
      const requested = positiveInt(candidate, null);
      if (requested && options.includes(requested)) return requested;
      return null;
    }

    // What the shopper last chose, where that is still a size this grid offers.
    // Numeric only — "All" is refused on the way in as well as on the way out,
    // so a stored "all" that arrived by some other route (an older build, a hand
    // edit, another tab) still cannot commit anyone to a whole collection on
    // sight. Writing and reading agree, rather than the rule living on one side
    // and being trusted from the other.
    rememberedPageSize() {
      const remembered = this.honourablePageSize(readStoredPageSize());
      return remembered === ALL_PAGE_SIZE ? null : remembered;
    }

    // The URL first, then what the shopper last chose, then the merchant's
    // setting. The URL leads because a shared link should show what it says it
    // shows; the remembered choice comes next so a preference outlives the
    // collection it was set on. Each step has to be honourable to count, so a
    // nonsense parameter falls through to the preference rather than displacing
    // it.
    readPageSize() {
      const fromUrl = this.honourablePageSize(this.urlParams().get(PAGE_SIZE_PARAM));
      if (fromUrl !== null) return fromUrl;

      const remembered = this.rememberedPageSize();
      if (remembered !== null) return remembered;

      return this.startingPageSize;
    }

    // Without an explicit display page, land on the one holding the first product
    // of the network page the server rendered. That is normally page 1, but a
    // shopper following the no-JS pager to ?page=5 arrives expecting the products
    // that link promised — not the top of the collection.
    readDisplayPage() {
      const requested = positiveInt(this.urlParams().get(DISPLAY_PAGE_PARAM), null);
      if (requested) return requested;
      const firstProduct = (this.networkPage - 1) * this.networkPageSize;
      return Math.floor(firstProduct / this.effectivePageSize) + 1;
    }

    // Both parameters are written together so a copied URL always says what it
    // shows. Never called on first load, so an untouched grid keeps a clean URL.
    writeState() {
      const url = new URL(window.location.href);
      url.searchParams.set(PAGE_SIZE_PARAM, String(this.pageSize));
      url.searchParams.set(DISPLAY_PAGE_PARAM, String(this.currentPage));
      // A network page number left over from the no-JS pager would tell the server to
      // render one we may not even want on the next load.
      url.searchParams.delete(NETWORK_PAGE_PARAM);

      // Carry the filter form's own record of the query string forward
      // untouched, so its popstate handler sees no change and doesn't refetch
      // the grid for what is purely a client-side move.
      const previous = history.state && history.state.searchParams;
      const searchParams =
        previous !== undefined
          ? previous
          : typeof FacetFiltersForm !== 'undefined'
          ? FacetFiltersForm.searchParamsPrev
          : undefined;

      history.pushState(
        { searchParams: searchParams, collectionPager: { pageSize: this.pageSize, page: this.currentPage } },
        '',
        url.toString()
      );
    }

    // The size a history entry stands for, which is not what readPageSize would
    // say. Storage holds the *newest* choice, and that is the wrong answer for an
    // older entry: consulting it on the way back would return the size the shopper
    // just left rather than the one they came from, leaving the earlier entry
    // unreachable and the Back button apparently broken.
    //
    // So each entry is asked what it stands for instead. Ones we wrote say so
    // outright, in the URL and again in their history state. The entry the
    // document loaded on predates every choice made since and records nothing, so
    // the size that was in force when we loaded stands in for it — which is the
    // remembered preference where there was one, and the merchant's setting where
    // there wasn't.
    restoredPageSize() {
      const fromUrl = this.honourablePageSize(this.urlParams().get(PAGE_SIZE_PARAM));
      if (fromUrl !== null) return fromUrl;

      const recorded = history.state && history.state.collectionPager;
      const fromHistory = recorded ? this.honourablePageSize(recorded.pageSize) : null;
      if (fromHistory !== null) return fromHistory;

      return this.loadedPageSize;
    }

    onPopState() {
      if (!this.isConnected) return;
      const size = this.restoredPageSize();
      const previousSize = this.pageSize;
      // Adopt the size being restored before reading the display page, not
      // after: where the URL carries no display page of ours, that size is what
      // the fallback arithmetic divides by, and the one being left would give
      // the wrong answer.
      this.pageSize = size;
      const page = this.readDisplayPage();
      if (size === previousSize && page === this.currentPage) return;
      this.currentPage = page;
      this.apply({ announce: true });
    }

    // ----------------------------------------------------------- the products

    list() {
      const grid = this.grid;
      if (!grid) return null;

      const marked = firstMatch(grid, LIST_SELECTORS);
      if (marked) return marked;

      // Last resort: any list that holds something recognisably product-shaped.
      const listWithProducts = Array.from(grid.querySelectorAll('ul')).find((ul) => {
        return ul.querySelector('[data-product-sku]') || ul.querySelector('.product-card-wrapper');
      });

      if (!listWithProducts) {
        console.warn('[Collection Pager] Could not find product list container in:', grid);
      }

      return listWithProducts || null;
    }

    // The list is occasionally not rendered yet when we first run.
    async waitForList(maxAttempts = 30) {
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const list = this.list();
        if (list) return list;
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      return null;
    }

    collectItems(list) {
      let items = Array.from(list.querySelectorAll(':scope > li'));

      // Grids that don't use <li> for their cards.
      if (items.length === 0) {
        items = Array.from(list.children).filter((child) => {
          return child.querySelector('[data-product-sku]') || child.classList.contains('product-card-wrapper');
        });
      }

      return items;
    }

    // Claim whatever the server rendered as the network page it says it is.
    adoptServerItems() {
      if (this.networkPageItems.size > 0) return this.loadedItems();
      const list = this.list();
      if (!list) return [];
      this.networkPageItems.set(this.networkPage, this.collectItems(list));
      return this.loadedItems();
    }

    loadedNetworkPages() {
      return Array.from(this.networkPageItems.keys()).sort((a, b) => a - b);
    }

    // Every product currently in the grid, in collection order.
    loadedItems() {
      const items = [];
      this.loadedNetworkPages().forEach((page) => {
        this.networkPageItems.get(page).forEach((item) => items.push(item));
      });
      return items;
    }

    // The network pages holding products at the given positions, [start, end).
    networkPagesFor(start, end) {
      const size = this.networkPageSize;
      const first = Math.floor(start / size) + 1;
      const last = Math.floor(Math.max(start, end - 1) / size) + 1;
      const pages = [];
      for (let page = first; page <= Math.min(last, this.networkPages); page += 1) pages.push(page);
      return pages;
    }

    // Every product this shopper may see that is actually in hand — the eligible
    // set where a provider has ruled on it, otherwise everything loaded.
    //
    // This is what "All" shows, taken directly rather than as a range, because a
    // range would be bounded by data-total-products and would hide any product
    // beyond it with no pager left to reach it by. Counting is still done
    // against the reported total, so a batch that failed to arrive shows up as a
    // short count in the announcement rather than disappearing quietly.
    availableItems() {
      return this.eligibleItems || this.loadedItems();
    }

    // Products at positions [start, end), in collection order. In filtered mode
    // positions index the eligible set; otherwise they index the collection, so
    // a product's position is derived from the network page it arrived in and
    // holds even when earlier ones were never fetched.
    itemsInRange(start, end) {
      if (this.eligibleItems) return this.eligibleItems.slice(start, end);

      const size = this.networkPageSize;
      const items = [];
      this.loadedNetworkPages().forEach((page) => {
        const base = (page - 1) * size;
        this.networkPageItems.get(page).forEach((item, index) => {
          const position = base + index;
          if (position >= start && position < end) items.push(item);
        });
      });
      return items;
    }

    loadNetworkPage(page) {
      if (this.networkPageItems.has(page)) return Promise.resolve();
      const existing = this.inFlight.get(page);
      if (existing) return existing;

      const request = fetch(networkPageUrl(page))
        .then((response) => (response.ok ? response.text() : null))
        .then((text) => {
          if (!text) return;
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const nextList = firstMatch(doc, FETCHED_LIST_SELECTORS);
          if (!nextList) return;
          const items = Array.from(nextList.querySelectorAll(':scope > li'));
          if (items.length === 0) return;
          this.insertNetworkPage(page, items);
        })
        .catch((e) => {
          console.warn('[Collection Pager] Failed to load network page', page, e);
        })
        .then(() => {
          this.inFlight.delete(page);
        });

      this.inFlight.set(page, request);
      return request;
    }

    insertNetworkPage(page, items) {
      const list = this.list();
      if (!list || this.networkPageItems.has(page)) return;
      this.networkPageItems.set(page, items);

      // Network pages arrive in whatever order the network returns them, so each
      // one is spliced in ahead of the earliest one that follows it.
      const later = this.loadedNetworkPages().find((candidate) => candidate > page);
      const anchor = later ? this.networkPageItems.get(later)[0] : null;
      items.forEach((item) => {
        item.style.display = 'none';
        list.insertBefore(item, anchor);
      });
    }

    // Fetches every missing network page at once. Never one after another.
    async ensureNetworkPages(pages) {
      const missing = pages.filter((page) => !this.networkPageItems.has(page));
      if (missing.length === 0) return false;
      await Promise.all(missing.map((page) => this.loadNetworkPage(page)));
      return true;
    }

    allNetworkPages() {
      return Array.from({ length: this.networkPages }, (_, i) => i + 1);
    }

    networkPagesForCurrentDisplayPage() {
      // Everything on one page needs every batch. Asked for directly rather than
      // derived from the page size, so a missing or wrong data-total-products
      // cannot quietly truncate the collection.
      if (this.showAllProducts) return this.allNetworkPages();
      const start = (this.currentPage - 1) * this.effectivePageSize;
      return this.networkPagesFor(start, start + this.effectivePageSize);
    }

    // ------------------------------------------------------------- rendering

    // Divide and show, using only what is already loaded. Resolves once the
    // products are actually on screen, which for a large reveal is several
    // animation frames later.
    async divideLoaded(options = {}) {
      if (this.showingEverything) return;
      const { announce = false, token = this.requestToken } = options;

      this.totalPages = Math.max(1, Math.ceil(this.totalProducts / this.effectivePageSize));
      this.currentPage = Math.min(Math.max(1, this.currentPage), this.totalPages);

      const start = (this.currentPage - 1) * this.effectivePageSize;
      const visible = this.showAllProducts
        ? this.availableItems()
        : this.itemsInRange(start, start + this.effectivePageSize);

      this.setNativeControlsVisible(false);
      // Present before it ever has content: a live region inserted together with
      // its text is not announced.
      this.statusNode();
      // Ahead of the reveal, not after: the pager is cheap, and a shopper should
      // be able to see where they are while the products are still arriving.
      this.renderPager();

      const settled = await this.reveal(visible, token);
      // A pass that was superseded mid-reveal leaves the announcement and the
      // render event to the pass that replaced it, so neither describes a grid
      // that has already moved on.
      if (!settled) return;

      if (announce) this.announce(start + 1, start + visible.length);

      this.dispatchEvent(
        new CustomEvent('collection-pager:render', {
          bubbles: true,
          detail: {
            currentPage: this.currentPage,
            totalPages: this.totalPages,
            pageSize: this.pageSize,
            eligible: this.totalProducts
          }
        })
      );
    }

    // Show exactly these products and hide the rest, resolving true once that is
    // done and false if a newer pass took over first.
    //
    // A handful of changes go through in one pass. Several hundred — which only
    // "All" on a large collection produces — are spread across animation frames
    // instead, so the browser lays out and paints a chunk at a time. The page
    // keeps scrolling and responding while the rest of the collection appears,
    // rather than freezing once and then arriving all at once.
    async reveal(visible, token) {
      const shown = new Set(visible);
      const changes = [];
      this.loadedItems().forEach((item) => {
        const display = shown.has(item) ? '' : 'none';
        if (item.style.display !== display) changes.push([item, display]);
      });

      const apply = (batch) => {
        batch.forEach(([item, display]) => {
          item.style.display = display;
        });
      };

      if (changes.length <= INCREMENTAL_REVEAL_THRESHOLD) {
        apply(changes);
        return true;
      }

      for (let index = 0; index < changes.length; index += REVEAL_CHUNK_SIZE) {
        // A shopper who changes their mind mid-reveal supersedes this pass. Stop
        // where we are and leave the grid to the pass that replaced us, rather
        // than finishing a reveal nobody asked for any more.
        if (token !== this.requestToken) return false;
        apply(changes.slice(index, index + REVEAL_CHUNK_SIZE));
        await nextFrame();
      }

      return token === this.requestToken;
    }

    // Whether this pass is enough work that the shopper should be told it is
    // working. Fetching always is; so is showing everything on a collection big
    // enough for the reveal itself to take several frames.
    get passNeedsIndicator() {
      return this.showAllProducts && this.totalProducts > INCREMENTAL_REVEAL_THRESHOLD;
    }

    // Fetch whatever the current display page needs, then divide and show.
    async apply(options = {}) {
      const token = ++this.requestToken;

      if (this.showingEverything) return;

      const list = this.list() || (await this.waitForList());
      if (!list || token !== this.requestToken) return;
      this.adoptServerItems();

      const needed = this.networkPagesForCurrentDisplayPage();
      const missing = needed.filter((page) => !this.networkPageItems.has(page));

      if (missing.length > 0 || this.passNeedsIndicator) {
        this.showSpinner();
        if (missing.length > 0) await this.ensureNetworkPages(needed);
        // Only the pass still holding the token owns the spinner, so a
        // superseded pass leaves it alone rather than clearing the indicator
        // belonging to the pass that replaced it.
        if (token !== this.requestToken) return;
      }

      await this.divideLoaded({ ...options, token });

      // Whichever pass settles the grid is the one that clears the indicator,
      // whether or not it was the pass that raised one. That is what stops a
      // change of mind mid-load from leaving a spinner behind: the newer pass
      // always finishes by sweeping it away.
      if (token === this.requestToken) this.hideSpinner();
    }

    // ------------------------------------------------------- shopper actions

    // Re-divides the products already on the page. Going back to page 1 keeps
    // this free of network requests: the first page at any offered size fits
    // inside the network page the server already sent.
    async setPageSize(size) {
      // A size this grid does not offer was never a choice, so it is ignored
      // outright rather than quietly becoming the merchant's setting — which
      // would both re-divide the grid nobody asked to re-divide and record that
      // setting as though the shopper had picked it.
      const next = this.honourablePageSize(size);
      if (next === null) return;

      // Remembered here and nowhere else, because this is the only moment a
      // shopper actually chooses a size. A URL that carries one is a deep link
      // being followed, not a preference being set, and history moving back
      // through a size is not a fresh choice either — neither should quietly
      // reconfigure anybody.
      //
      // Written ahead of the early return below, so re-picking the size already
      // on screen still records it. That is a real choice whenever the size came
      // from the merchant's setting rather than from them, and dropping it would
      // lose the preference the moment they hit a collection that starts at
      // something else.
      //
      // "All" is never written. It belongs to the collection it was chosen on, so
      // the last numeric choice stays the one that carries forward — nobody
      // should inherit several hundred products on every future visit from a
      // single click.
      if (next !== ALL_PAGE_SIZE) storePageSize(next);

      const unchanged = next === this.pageSize && this.currentPage === 1;
      this.pageSize = next;
      this.currentPage = 1;

      // Re-picking the size already showing is normally nothing to do. It is not
      // nothing when a batch never arrived: with no pager to reach the missing
      // products by, picking the size again is the shopper's only way of asking
      // for them, and the pass that follows refetches exactly what is absent.
      const complete = this.networkPagesForCurrentDisplayPage().every((page) => this.networkPageItems.has(page));
      if (unchanged && complete) return;

      this.writeState();
      await this.apply({ announce: true });
    }

    async goToPage(page) {
      const target = Math.min(Math.max(1, page), this.totalPages);
      if (target === this.currentPage) return;
      this.currentPage = target;
      this.writeState();
      await this.apply({ announce: true });
      this.scrollGridIntoView();
    }

    // Following a page link would have landed the shopper at the top of the new
    // page; re-dividing in place would leave them at the bottom of it.
    scrollGridIntoView() {
      const grid = this.grid;
      if (!grid || typeof grid.getBoundingClientRect !== 'function') return;
      if (grid.getBoundingClientRect().top >= 0) return;
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // -------------------------------------------------------- the pager itself

    // Everything the server rendered for pagination or lazy loading, minus our
    // own replacement pager.
    nativeControls() {
      const grid = this.grid;
      if (!grid) return [];
      return Array.from(grid.querySelectorAll('.pagination-wrapper, .js-load-more, .js-infinite-scroll')).filter(
        (el) => !this.contains(el)
      );
    }

    setNativeControlsVisible(visible) {
      this.nativeControls().forEach((el) => {
        el.style.display = visible ? '' : 'none';
      });
    }

    setAllItemsVisible(visible) {
      this.loadedItems().forEach((item) => {
        item.style.display = visible ? '' : 'none';
      });
    }

    // The live region outlives the pager markup, so an announcement is never
    // lost to a re-render.
    statusNode() {
      let node = this.querySelector('.collection-pager__status');
      if (!node) {
        node = document.createElement('p');
        node.className = 'collection-pager__status visually-hidden';
        node.setAttribute('role', 'status');
        node.setAttribute('aria-live', 'polite');
        this.insertBefore(node, this.firstChild);
      }
      return node;
    }

    navNode() {
      let node = this.querySelector('.collection-pager__nav');
      if (!node) {
        node = document.createElement('div');
        node.className = 'collection-pager__nav';
        this.appendChild(node);
      }
      return node;
    }

    announce(first, last) {
      const template = strings().showingRange;
      if (!template) return;
      this.statusNode().textContent = interpolate(template, {
        first: first,
        last: last,
        count: this.totalProducts
      });
    }

    renderPager() {
      const nav = this.navNode();

      // Everything on one page has no pages to move between, so no pager is
      // rendered at all — not an empty one, and not a disabled one.
      if (this.showAllProducts || this.totalPages <= 1) {
        nav.innerHTML = '';
        return;
      }

      // Ours is the only pager on screen while we're in charge.
      const nativeNav = this.nativeControls()
        .map((el) => el.querySelector('nav.pagination'))
        .find(Boolean);
      const navLabel = strings().pagination || (nativeNav && nativeNav.getAttribute('aria-label')) || 'Pagination';

      // What the shopper was on before the markup is replaced, so focus can
      // land somewhere equivalent afterwards rather than falling to the body.
      const active = document.activeElement;
      const hadFocus = !!(active && nav.contains(active));
      const focusedAction = hadFocus ? active.getAttribute('data-page-action') : null;

      const currentPage = this.currentPage;
      const totalPages = this.totalPages;
      const pageLabel = (page) => interpolate(strings().page || 'Page [number]', { number: page });

      const prevHtml =
        currentPage > 1
          ? `<li><button type="button" class="pagination__item--prev pagination__item-arrow link motion-reduce" data-page-action="prev" aria-label="${
              strings().previous || 'Previous page'
            }"><svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" role="img"><path d="M10.707 2.293a1 1 0 0 1 0 1.414L6.414 8l4.293 4.293a1 1 0 0 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0z"/></svg></button></li>`
          : '';

      const nextHtml =
        currentPage < totalPages
          ? `<li><button type="button" class="pagination__item--next pagination__item-arrow link motion-reduce" data-page-action="next" aria-label="${
              strings().next || 'Next page'
            }"><svg aria-hidden="true" focusable="false" viewBox="0 0 16 16" role="img"><path d="M5.293 2.293a1 1 0 0 0 0 1.414L9.586 8l-4.293 4.293a1 1 0 1 0 1.414 1.414l5-5a1 1 0 0 0 0-1.414l-5-5a1 1 0 0 0-1.414 0z"/></svg></button></li>`
          : '';

      const pagesHtml = paginationParts(totalPages, currentPage)
        .map((part) => {
          if (part === '...') {
            return '<li><span class="pagination__item">...</span></li>';
          }
          if (part === currentPage) {
            return `<li><span class="pagination__item pagination__item--current" aria-current="page" aria-label="${pageLabel(
              part
            )}" tabindex="-1">${part}</span></li>`;
          }
          return `<li><button type="button" class="pagination__item link" data-page="${part}" aria-label="${pageLabel(
            part
          )}"><span title="${part}">${part}</span></button></li>`;
        })
        .join('');

      nav.innerHTML = `<div class="pagination-wrapper collection-pager__wrapper"><nav class="pagination" role="navigation" aria-label="${navLabel}"><ul class="pagination__list list-unstyled" role="list">${prevHtml}${pagesHtml}${nextHtml}</ul></nav></div>`;

      nav.querySelectorAll('[data-page]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const page = Number(btn.getAttribute('data-page'));
          if (!Number.isFinite(page) || page < 1) return;
          this.goToPage(page);
        });
      });

      nav.querySelectorAll('[data-page-action]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const action = btn.getAttribute('data-page-action');
          if (action === 'prev') this.goToPage(currentPage - 1);
          if (action === 'next') this.goToPage(currentPage + 1);
        });
      });

      if (hadFocus) this.restoreFocus(focusedAction);
    }

    // Prefer the arrow the shopper was already using; when it has gone —
    // because this is now the first or last page — the current-page indicator
    // keeps focus inside the pager and reads out where they landed.
    restoreFocus(action) {
      const nav = this.navNode();
      const target =
        (action && nav.querySelector(`[data-page-action="${action}"]`)) ||
        nav.querySelector('.pagination__item--current');
      // preventScroll so focus doesn't drag the viewport down to the pager and
      // undo scrollGridIntoView.
      if (target) target.focus({ preventScroll: true });
    }

    // ---------------------------------------------------------- filtered mode

    showSpinner(label) {
      const grid = this.grid;
      if (!grid) return;

      // Never stack spinners — one at a time, freshly built.
      this.hideSpinner();

      const text = label || strings().loading || DEFAULT_LOADING_LABEL;
      const spinner = document.createElement('div');
      spinner.className = SPINNER_CLASS;
      spinner.setAttribute('role', 'status');
      spinner.setAttribute('aria-label', text);
      spinner.innerHTML = `
      <div class="${SPINNER_CLASS}__content">
        <div class="${SPINNER_CLASS}__circle"></div>
        <p class="${SPINNER_CLASS}__text">${text}</p>
      </div>
    `;

      grid.appendChild(spinner);
    }

    // Sweeps the whole document rather than tracking one reference, so a spinner
    // orphaned by a mode change or a re-render can't survive.
    hideSpinner() {
      document.querySelectorAll(`.${SPINNER_CLASS}`).forEach((el) => {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
    }

    // Whether this pass should stop. Only the pass still holding the token
    // manages the spinner, so a superseded pass never clears the spinner
    // belonging to the pass that replaced it.
    superseded(token, provider) {
      if (token !== this.requestToken) return true;
      if (!provider.isActive()) {
        this.hideSpinner();
        return true;
      }
      return false;
    }

    // Gather every network page, ask who's eligible, divide into display pages.
    // Eligibility is a verdict on the whole collection, so unlike the plain path
    // this cannot fetch lazily — but it fetches all at once.
    async refresh(options = {}) {
      const { force = false } = options;
      const token = ++this.requestToken;
      const provider = eligibilityProvider;

      // Nobody is filtering this grid. Divide it ourselves where display pages
      // are ours to own; otherwise hand it back to native pagination.
      if (!provider || !provider.isActive()) {
        this.eligibleItems = null;
        this.showingEverything = false;
        if (this.ownsPagination) {
          await this.apply();
        } else {
          this.release();
        }
        return;
      }

      const list = this.list() || (await this.waitForList());
      if (!list || token !== this.requestToken) return;

      this.showingEverything = false;
      this.eligibleItems = null;
      this.pageSize = this.readPageSize();
      this.currentPage = this.readDisplayPage();

      this.hideSpinner();
      this.showSpinner(provider.loadingLabel);

      // Blank the grid while we work — a half-filtered grid must never be shown.
      this.adoptServerItems();
      this.setAllItemsVisible(false);
      this.setNativeControlsVisible(false);

      await this.ensureNetworkPages(this.allNetworkPages());
      if (this.superseded(token, provider)) return;

      const items = this.loadedItems();
      if (items.length === 0) {
        this.hideSpinner();
        return;
      }

      let eligible = null;
      try {
        eligible = await provider.eligible(items, { force });
      } catch (e) {
        console.warn('[Collection Pager] Eligibility provider failed:', e);
      }

      if (this.superseded(token, provider)) return;

      // A provider with no opinion means every product is eligible.
      this.eligibleItems = Array.isArray(eligible) ? eligible : items;

      this.setAllItemsVisible(false);
      await this.divideLoaded({ token });
      if (this.superseded(token, provider)) return;
      this.hideSpinner();
    }

    // Show every product in the grid unfiltered and unpaged, ignoring both
    // eligibility and the shopper's chosen size. This is the Showroom's "show all
    // products" toggle, not the "All" page size — that one is a size like any
    // other and goes through setPageSize.
    showEverything() {
      this.requestToken += 1;
      this.showingEverything = true;
      this.navNode().innerHTML = '';
      this.setAllItemsVisible(true);
      this.setNativeControlsVisible(false);
      this.hideSpinner();
    }

    // Blank the grid ahead of a refresh so nothing stale is left on screen.
    hideAll() {
      this.navNode().innerHTML = '';
      this.setAllItemsVisible(false);
      this.setNativeControlsVisible(false);
    }

    // Hand the grid back to native pagination.
    release() {
      this.requestToken += 1;
      this.showingEverything = false;
      this.eligibleItems = null;
      this.navNode().innerHTML = '';
      this.setAllItemsVisible(true);
      this.setNativeControlsVisible(true);
      this.hideSpinner();
    }
  }

  customElements.define('collection-pager', CollectionPager);

  window.CollectionPager = {
    setEligibilityProvider(provider) {
      eligibilityProvider = provider || null;
    },
    // The grid on a page has one pager; callers that own eligibility use this to
    // drive it without caring where in the markup it sits.
    current() {
      const el = document.querySelector('collection-pager');
      return el && typeof el.refresh === 'function' ? el : null;
    }
  };
})();
